/**
 * What an app's users run, read in the window (RFC 0313 3m).
 *
 * An app with a live version draws its view as that version saved it, not as
 * its view document reads now, so an edit never ships itself. The author can
 * look at their edits before installing them: Preview edits, per app, in this
 * window only. Everyone else keeps running the installed version.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import { isValidDocumentId } from '@automerge/automerge-repo/slim'
import {
  atLiveVersion,
  installedViewDocId,
  projectString,
  readApp,
  readVersions,
} from '@mod/core/model'
import { useMemo, useSyncExternalStore } from 'react'
import { useDocument } from '../world/repo/useDocument'

const KEY = 'mod_preview_edits'
const listeners = new Set<() => void>()

function read(): string[] {
  try {
    const raw = localStorage.getItem(KEY)
    const v = raw ? (JSON.parse(raw) as unknown) : []
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []
  } catch {
    return []
  }
}

let cache = read()

/** Turn Preview edits on or off for one app, in this window. */
export function setPreviewEdits(appId: string, on: boolean): void {
  const next = new Set(cache)
  if (on) next.add(appId)
  else next.delete(appId)
  cache = [...next]
  try {
    localStorage.setItem(KEY, JSON.stringify(cache))
  } catch {
    // A window that cannot persist still previews until it reloads.
  }
  for (const fn of listeners) fn()
}

export function usePreviewEdits(appId: string | undefined): boolean {
  const list = useSyncExternalStore(
    (fn) => {
      listeners.add(fn)
      return () => listeners.delete(fn)
    },
    () => cache,
  )
  return !!appId && list.includes(appId)
}

/**
 * The document an app's users see: `doc` at the app's live version, unless the
 * app has none, the version does not hold it, or this window previews edits.
 */
export function useLiveVersion<T>(
  projectId: string,
  appId: string | undefined,
  documentId: string,
  doc: T | undefined,
): T | undefined {
  const [appDoc] = useDocument(appId as AnyDocumentId | undefined)
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  const storedVersionsId = projectString(
    (projectDoc as { versionsDocId?: unknown } | undefined)?.versionsDocId,
  )
  // CHECKED BEFORE IT IS OPENED. A pointer stored on a project can be one
  // Automerge will not accept: an id written by an older build, or one whose
  // document never existed. `useDocument` throws on it rather than returning
  // nothing, and a throw here takes `DocumentView` down and the window with
  // it. A pointer we cannot open is the same as no pointer.
  const versionsId =
    storedVersionsId && isValidDocumentId(String(storedVersionsId))
      ? String(storedVersionsId)
      : undefined
  const [versionsDoc] = useDocument<unknown>(
    versionsId as AnyDocumentId | undefined,
  )
  const previewing = usePreviewEdits(appId)
  return useMemo(() => {
    if (previewing) return doc
    const live = readApp(appDoc)?.live
    if (!live) return doc
    const version = readVersions(versionsDoc).find((v) => v.id === live)
    // Installed, but its version has not loaded: wait, never run the draft.
    if (!version) return undefined
    return atLiveVersion(doc, documentId, version)
  }, [previewing, appDoc, versionsDoc, doc, documentId])
}

/**
 * The view document an installed app runs: the one its live version named,
 * whatever the app record names now (finding 6). The app's current view when
 * it has no live version, or this window previews edits.
 */
export function useInstalledViewId(
  projectId: string,
  appId: string | undefined,
  currentViewId: string | undefined,
): string | undefined {
  const [appDoc] = useDocument(appId as AnyDocumentId | undefined)
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  const versionsId = projectString(
    (projectDoc as { versionsDocId?: unknown } | undefined)?.versionsDocId,
  )
  const [versionsDoc] = useDocument<unknown>(
    (versionsId ?? undefined) as AnyDocumentId | undefined,
  )
  const previewing = usePreviewEdits(appId)
  return useMemo(() => {
    if (!appId || previewing) return currentViewId
    const live = readApp(appDoc)?.live
    if (!live) return currentViewId
    const version = readVersions(versionsDoc).find((v) => v.id === live)
    if (!version) return undefined
    return installedViewDocId(appDoc, appId, version) ?? currentViewId
  }, [appId, previewing, appDoc, versionsDoc, currentViewId])
}
